import { loadSetting, saveSetting } from './settingsService';
import type { Arbeitsplatz } from '../modules/vorsorge/components/ArbeitsplatzVerwaltung';
import { TATBESTAND_MAP } from '../modules/vorsorge/data/tatbestaende';

// ── Arbeitsplätze ─────────────────────────────────────────────────────────

const KEY_AP = 'arbeitsplaetze_v1';

export async function loadArbeitsplaetze(mandantId?: string): Promise<Arbeitsplatz[]> {
  const all = await loadSetting<Arbeitsplatz[]>(KEY_AP, []);
  return mandantId !== undefined ? all.filter(a => a.mandantId === mandantId) : all;
}

export async function saveArbeitsplatz(ap: Arbeitsplatz): Promise<void> {
  const all = await loadSetting<Arbeitsplatz[]>(KEY_AP, []);
  const idx = all.findIndex(x => x.id === ap.id);
  if (idx >= 0) { all[idx] = ap; } else { all.push(ap); }
  await saveSetting(KEY_AP, all);
}

export async function deleteArbeitsplatz(id: string): Promise<void> {
  const all = await loadSetting<Arbeitsplatz[]>(KEY_AP, []);
  await saveSetting(KEY_AP, all.filter(x => x.id !== id));
}

// ── VGÜ-Fälligkeiten (pseudonymisiert) ───────────────────────────────────

export interface VguFaelligkeit {
  id:              string;
  mandantId:       string;
  arbeitsplatzId:  string;
  mitarbeiterKennung: string;   // z.B. "MA-001" — kein Klarname!
  tatbestandCode:  string;
  faelligkeitDatum: string;     // ISO-Date
  letztesDatum?:   string;
  status:          'OFFEN' | 'ERLEDIGT' | 'UEBERFAELLIG';
  erstelltAm:      string;
  geaendertAm:     string;
}

const KEY_VGU = 'vgu_faelligkeiten_v1';

export async function loadFaelligkeiten(mandantId?: string): Promise<VguFaelligkeit[]> {
  const all = await loadSetting<VguFaelligkeit[]>(KEY_VGU, []);
  const today = new Date().toISOString().slice(0, 10);
  // Auto-update UEBERFAELLIG
  let changed = false;
  for (const f of all) {
    if (f.status === 'OFFEN' && f.faelligkeitDatum < today) {
      f.status = 'UEBERFAELLIG';
      f.geaendertAm = new Date().toISOString();
      changed = true;
    }
  }
  if (changed) { await saveSetting(KEY_VGU, all); }
  return mandantId !== undefined ? all.filter(f => f.mandantId === mandantId) : all;
}

export async function saveFaelligkeit(f: VguFaelligkeit): Promise<void> {
  const all = await loadSetting<VguFaelligkeit[]>(KEY_VGU, []);
  const idx = all.findIndex(x => x.id === f.id);
  if (idx >= 0) { all[idx] = f; } else { all.push(f); }
  await saveSetting(KEY_VGU, all);
}

export async function deleteFaelligkeit(id: string): Promise<void> {
  const all = await loadSetting<VguFaelligkeit[]>(KEY_VGU, []);
  await saveSetting(KEY_VGU, all.filter(x => x.id !== id));
}

export async function markErledigt(id: string): Promise<void> {
  const all = await loadSetting<VguFaelligkeit[]>(KEY_VGU, []);
  const f = all.find(x => x.id === id);
  if (f !== undefined) {
    const today = new Date().toISOString().slice(0, 10);
    f.status = 'ERLEDIGT';
    f.letztesDatum = today;
    f.geaendertAm  = new Date().toISOString();
    await saveSetting(KEY_VGU, all);
  }
}

// Erledigt markieren + nächsten Folgetermin automatisch anlegen
export async function markErledigtMitFolgetermin(
  id: string,
  untersuchungsDatum: string,  // ISO-Date "YYYY-MM-DD"
): Promise<VguFaelligkeit | null> {
  const all = await loadSetting<VguFaelligkeit[]>(KEY_VGU, []);
  const f   = all.find(x => x.id === id);
  if (f === undefined) { return null; }

  // 1) Original als erledigt markieren
  f.status       = 'ERLEDIGT';
  f.letztesDatum = untersuchungsDatum;
  f.geaendertAm  = new Date().toISOString();

  // 2) Nächsten Termin berechnen
  const tb = TATBESTAND_MAP.get(f.tatbestandCode);
  let naechste: VguFaelligkeit | null = null;
  if (tb !== undefined) {
    const basis  = new Date(untersuchungsDatum);
    basis.setMonth(basis.getMonth() + tb.intervallMonate);
    const naechstesDatum = basis.toISOString().slice(0, 10);
    naechste = {
      id:                  crypto.randomUUID(),
      mandantId:           f.mandantId,
      arbeitsplatzId:      f.arbeitsplatzId,
      mitarbeiterKennung:  f.mitarbeiterKennung,
      tatbestandCode:      f.tatbestandCode,
      faelligkeitDatum:    naechstesDatum,
      letztesDatum:        untersuchungsDatum,
      status:              'OFFEN',
      erstelltAm:          new Date().toISOString(),
      geaendertAm:         new Date().toISOString(),
    };
    all.push(naechste);
  }

  await saveSetting(KEY_VGU, all);
  return naechste;
}

export async function nextMaKennung(mandantId: string): Promise<string> {
  const all = await loadSetting<VguFaelligkeit[]>(KEY_VGU, []);
  const used = all
    .filter(f => f.mandantId === mandantId)
    .map(f => f.mitarbeiterKennung)
    .filter((v, i, a) => a.indexOf(v) === i);
  const nums = used
    .map(k => parseInt(k.replace(/[^0-9]/g, ''), 10))
    .filter(n => !isNaN(n));
  const next = nums.length === 0 ? 1 : Math.max(...nums) + 1;
  return `MA-${String(next).padStart(3, '0')}`;
}
